import {
  LinkButton,
  ResponsiveImage,
  SubsectionTitle,
} from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { CopyParagraph } from '@sleepstation/components-copy'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import {
  DIRECTION,
  LAYOUT_CHILD,
  LAYOUT_SPACING,
  SIZE,
} from '@sleepstation/const'
import {
  baseline,
  mq,
  padding,
  paddingRight,
  themeColor,
} from '@sleepstation/style-api-helpers'
import { outset } from '@sleepstation/style-mixins'
import { reversedTheme } from '@sleepstation/themes-public-site'
import PropTypes from 'prop-types'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Title = styled(SubsectionTitle)``

const Wrapper = styled(LinkBase).attrs(({ id }) => ({
  'data-cy': `${id}-card`,
}))`
  text-decoration: none;
`

const Layout = styled(BreakingLayout).attrs(({ id }) => ({
  spacing: [
    LAYOUT_SPACING.MEDIUM,
    LAYOUT_SPACING.MEDIUM,
    LAYOUT_SPACING.MEDIUM,
  ],
  direction: [DIRECTION.VERTICAL, DIRECTION.VERTICAL, DIRECTION.HORIZONTAL],
  child: [LAYOUT_CHILD.FIGHT, LAYOUT_CHILD.FIGHT, LAYOUT_CHILD.FIGHT],
  'data-cy': `${id}-card`,
}))`
  position: relative;
  ${padding([1, 1, 1.25])}
  ${outset(SIZE.LARGE, `background`)}
  background:
    linear-gradient(
      ${themeColor(`backgroundStart`)},
      ${themeColor(`backgroundEnd`)}
    );
  background-position: top right;
  background-repeat: no-repeat;
  background-size: 100%, 100%;

  ${mq(`mediumUp`)} {
    background: linear-gradient(
      ${themeColor(`backgroundStart`)},
      ${themeColor(`backgroundEnd`)}
    );
    background-position: top right;
    background-repeat: no-repeat;
  }
`

const Primary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
})`
  ${CopyParagraph} {
    ${baseline(1)}

    ${mq(`mediumUp`)} {
      margin-top: 12px;
    }
  }
  ${mq(`mediumUp`)} {
    ${paddingRight(2)}
    margin-left: 0;
    order: 1;
  }
`

const Secondary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
})`
  ${mq(`mediumUp`)} {
    flex-basis: 25%;
    flex-shrink: 0;
    margin-left: auto;
    order: 2;
  }
`

const Header = styled(VLayout).attrs({
  as: `header`,
  spacing: LAYOUT_SPACING.SMALL,
})``

const Footer = styled.footer`
  > * {
    width: 100%;

    ${mq(`smallUp`)} {
      max-width: 300px;
    }
  }
`

const ImageWrapper = styled.div`
  > * {
    &:first-child {
      width: 57px;
      ${mq(`mediumUp`)} {
        display: none;
      }
    }

    &:nth-child(2) {
      display: none;
      max-width: 97px;
      margin-left: auto;
      ${mq(`mediumUp`)} {
        display: block;
      }
    }

    &:only-child {
      ${mq(`mediumUp`)} {
        display: block;
      }
    }
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const InfoImageCard = ({
  title,
  id,
  primary,
  secondary,
  link,
  shouldShowLink,
}) => (
  <ThemeProvider theme={reversedTheme}>
    <Wrapper>
      <Layout id={id}>
        <Secondary>
          <ImageWrapper>
            <ResponsiveImage
              aspectRatio={secondary.image.default.aspectRatio}
              src={secondary.image.default.url}
              alt={secondary.image.default.alt}
            />
            <ResponsiveImage
              aspectRatio={secondary.image.mediumUp.aspectRatio}
              src={secondary.image.mediumUp.url}
              alt={secondary.image.mediumUp.alt}
            />
          </ImageWrapper>
        </Secondary>
        <Primary>
          <Header>
            <Title>{title}</Title>
            <CopyParagraph>{primary.content}</CopyParagraph>
            {primary.subContent && (
              <CopyParagraph>{primary.subContent}</CopyParagraph>
            )}
          </Header>
          {shouldShowLink && (
            <Footer>
              <LinkButton as="div" size={SIZE.SMALL} data-cy="card-next-link">
                {link.label}
              </LinkButton>
            </Footer>
          )}
        </Primary>
      </Layout>
    </Wrapper>
  </ThemeProvider>
)

InfoImageCard.propTypes = {
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  primary: PropTypes.object.isRequired,
  secondary: PropTypes.object.isRequired,
  link: PropTypes.object.isRequired,
  shouldShowLink: PropTypes.bool,
}

InfoImageCard.defaultProps = {
  shouldShowLink: true,
}

export default InfoImageCard
